import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Check, Copy, ArrowRight, Loader2, Mail, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [paymentData, setPaymentData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [pollAttempts, setPollAttempts] = useState(0);
  const maxPollAttempts = 10;
  const pollInterval = 2000; // 2 secondes

  const verifyPayment = useCallback(async (sessionId, attempt = 0) => {
    try {
      const response = await axios.get(`${API_URL}/payment/status/${sessionId}`);
      const data = response.data;
      
      // Si le paiement est confirmé
      if (data.payment_status === 'paid' && data.code) {
        setPaymentData(data);
        setLoading(false);
        // Track Purchase event (Meta Pixel + GA4)
        if (typeof window !== 'undefined' && window.fbq) {
          window.fbq('track', 'Purchase', {
            content_name: 'Déclic RePas',
            value: data.plan === 'accompagnement' ? 190 : 39,
            currency: 'EUR',
            content_type: 'product'
          });
        }
        if (typeof window !== 'undefined' && window.gtag) {
          window.gtag('event', 'purchase', {
            transaction_id: sessionId,
            value: data.plan === 'accompagnement' ? 190 : 39,
            currency: 'EUR',
            items: [{ item_name: 'Déclic RePas', price: data.plan === 'accompagnement' ? 190 : 39 }]
          });
        }
        return;
      }
      
      // Si le paiement a échoué ou expiré
      if (data.status === 'expired' || data.payment_status === 'unpaid') {
        setPaymentData({ error: true, message: 'Le paiement a échoué ou a expiré.' });
        setLoading(false);
        return;
      }
      
      // Continuer le polling si pas encore confirmé
      if (attempt < maxPollAttempts) {
        setPollAttempts(attempt + 1);
        setTimeout(() => verifyPayment(sessionId, attempt + 1), pollInterval);
      } else {
        // Timeout - trop de tentatives
        setPaymentData({ 
          error: true, 
          message: 'La vérification du paiement a pris trop de temps. Veuillez vérifier votre email ou contacter le support.' 
        });
        setLoading(false);
      }
    } catch (error) {
      console.error('Erreur vérification:', error);
      if (attempt < maxPollAttempts) {
        setTimeout(() => verifyPayment(sessionId, attempt + 1), pollInterval);
      } else {
        setPaymentData({ error: true, message: 'Erreur lors de la vérification du paiement.' });
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      verifyPayment(sessionId, 0);
    } else {
      setLoading(false);
    }
  }, [searchParams, verifyPayment]);

  const copyCode = () => {
    if (paymentData?.code) {
      navigator.clipboard.writeText(paymentData.code);
      setCopied(true);
      toast.success('Code copié !');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-sand-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-sage-500 animate-spin mx-auto mb-4" />
          <p className="text-sage-600">Vérification du paiement...</p>
          {pollAttempts > 0 && (
            <p className="text-sage-500 text-sm mt-2">Tentative {pollAttempts}/{maxPollAttempts}</p>
          )}
        </div>
      </div>
    );
  }

  // Afficher une erreur si le paiement a échoué
  if (paymentData?.error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-sage-500 to-sage-600 flex items-center justify-center p-6">
        <div className="bg-white rounded-3xl p-8 max-w-md w-full text-center shadow-xl">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-10 h-10 text-red-600" />
          </div>
          <h1 className="text-2xl font-serif font-light text-sage-700 mb-4">
            Oups, un problème est survenu
          </h1>
          <p className="text-sage-600 mb-6">{paymentData.message}</p>
          <Button
            onClick={() => navigate('/landing')}
            className="w-full h-12 bg-sage-500 hover:bg-sage-600 text-white rounded-xl"
          >
            Retourner à l'accueil
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-sage-500 to-sage-600 flex items-center justify-center p-6">
      <div className="bg-white rounded-3xl p-8 max-w-md w-full text-center shadow-xl">
        {/* Success icon */}
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <Check className="w-10 h-10 text-green-600" />
        </div>

        <h1 className="text-3xl font-serif font-light text-sage-700 mb-2">
          Merci pour votre achat !
        </h1>
        
        <p className="text-sage-600 mb-6">
          Votre paiement a été confirmé. Bienvenue dans le programme Déclic RePas !
        </p>

        {paymentData?.code && (
          <div className="bg-sage-50 rounded-xl p-6 mb-6">
            <p className="text-sm text-sage-600 mb-2">Votre code d'accès</p>
            <div className="flex items-center justify-center gap-3">
              <span className="text-3xl font-mono font-bold text-sage-700 tracking-wider">
                {paymentData.code}
              </span>
              <Button
                variant="ghost"
                size="icon"
                onClick={copyCode}
                className="text-sage-500 hover:text-sage-700"
              >
                {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
              </Button>
            </div>
            
            {paymentData.email && (
              <div className="flex items-center justify-center gap-2 mt-4 text-sm text-sage-500">
                <Mail className="w-4 h-4" />
                <span>Envoyé à {paymentData.email}</span>
              </div>
            )}
          </div>
        )}

        <div className="space-y-3">
          <Button
            onClick={() => navigate('/signup')}
            className="w-full h-14 bg-declic hover:bg-declic/90 text-white rounded-xl text-lg"
          >
            Créer mon compte
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
          
          <Button
            onClick={() => navigate('/login')}
            variant="outline"
            className="w-full h-12 border-sage-300 text-sage-600 rounded-xl"
          >
            J'ai déjà un compte
          </Button>
        </div>

        <p className="text-xs text-sage-500 mt-6">
          Conservez ce code précieusement. Il vous sera demandé lors de la création de votre compte.
        </p>
      </div>
    </div>
  );
};

export default PaymentSuccess;
